// src/utils/documentUtils.ts

import axios from "axios";
import CryptoJS from "crypto-js";
import JSZip from "jszip";
import type { CostBreakdown, FileData, ProcessingJob } from "@/types";
import { safeStringify } from "@/utils/api";

export const MIN_FILE_SIZE = 1024; // 1 KB

export const createSafeFileName = (documentTitle: string, totalPages: number): string => {
  let safeName = documentTitle
    .replace(/[^a-z0-9]/gi, "_")
    .replace(/_+/g, "_")
    .toLowerCase();
  safeName = safeName.replace(/^_+|_+$/g, "");

  const maxFileNameLength = 255;
  const baseName = `Combined_Document_Output_File_Bundle__Document__`;
  const totalPagesStr = `__Pages_${totalPages}`;

  const maxSafeNameLength = maxFileNameLength - baseName.length - totalPagesStr.length;
  if (safeName.length > maxSafeNameLength) {
    safeName = safeName.substring(0, maxSafeNameLength);
  }

  return `${baseName}${safeName}${totalPagesStr}`;
};

/**
 * Creates a hosted page identifier string by sanitizing the document title and hashing the content.
 * @param title - The title of the document.
 * @param content - The content of the document.
 * @returns A unique identifier string for the hosted page.
 */
export const createHostedPageIdentifierString = (title: string, content: string): string => {
  // Create a safe slug from the title
  const safeSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumerics with hyphens
    .replace(/^-|-$/g, "") // Remove leading/trailing hyphens
    .substring(0, 50); // Limit length to 50 characters

  // Create a hash of the content
  const hash = CryptoJS.SHA256(content).toString(CryptoJS.enc.Hex).substring(0, 6); // Extract first 6 characters of the hash

  // Combine slug and hash
  return `${safeSlug}-${hash}`;
};

/**
 * Creates a hosted page identifier string by sanitizing the document title and using a provided file hash.
 * @param title - The title of the document.
 * @param fileHash - The precomputed hash of the file content (expected as a hexadecimal string).
 * @returns A unique identifier string for the hosted page.
 */
export const createHostedPageIdentifierFromHash = (title: string, fileHash: string): string => {
  // Create a safe slug from the title
  const safeSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumerics with hyphens
    .replace(/^-|-$/g, "") // Remove leading/trailing hyphens
    .substring(0, 50); // Limit length to 50 characters

  // Ensure the fileHash is in lowercase to maintain consistency
  const normalizedHash = fileHash.toLowerCase();

  // Extract the first 6 characters of the hash
  const hashSegment = normalizedHash.substring(0, 6);

  // Combine slug and hash segment
  return `${safeSlug}-${hashSegment}`;
};

export const calculateCostBreakdown = (
  wordCount: number,
  selectedOptions: {
    generateMultipleChoiceQuiz: boolean;
    generateShortAnswerQuiz: boolean;
    generateAnkiFlashcards: boolean;
    generatePresentationSlides: boolean;
    generateSummary: boolean;
    generateMindmap: boolean;
    generateReadabilityAnalysis: boolean;
    generateLessonPlan: boolean;
  },
): CostBreakdown => {
  const MINIMUM_BASE_COST_IN_CREDITS = parseInt(
    process.env.NEXT_PUBLIC_MINIMUM_BASE_COST_IN_CREDITS || "2",
    10,
  );
  const NUMBER_OF_CREDITS_PER_1000_WORDS = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_1000_WORDS || "3",
    10,
  );
  const NUMBER_OF_CREDITS_PER_QUIZ = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_PER_QUIZ || "5",
    10,
  );
  const NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES || "3",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS || "4",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES || "4",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_SUMMARY = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_SUMMARY || "5",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_MINDMAP = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_MINDMAP || "12",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_READABILITY_ANALYSIS = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_READABILITY_ANALYSIS || "10",
    10,
  );
  const NUMBER_OF_CREDITS_FOR_LESSON_PLAN = parseInt(
    process.env.NEXT_PUBLIC_NUMBER_OF_CREDITS_FOR_LESSON_PLAN || "6",
    10,
  );
  const GLOBAL_PRICING_MULTIPLIER = parseFloat(
    process.env.NEXT_PUBLIC_GLOBAL_PRICING_MULTIPLIER || "0.8",
  );

  const baseCost =
    wordCount === 0
      ? 0
      : Math.max(
          MINIMUM_BASE_COST_IN_CREDITS,
          Math.ceil(wordCount / 1000) * NUMBER_OF_CREDITS_PER_1000_WORDS,
        );

  const multipleChoiceCost = selectedOptions.generateMultipleChoiceQuiz
    ? Math.ceil(NUMBER_OF_CREDITS_PER_QUIZ * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const shortAnswerCost = selectedOptions.generateShortAnswerQuiz
    ? Math.ceil(NUMBER_OF_CREDITS_PER_QUIZ * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const ankiFlashcardsCost = selectedOptions.generateAnkiFlashcards
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_ANKI_FLASHCARDS * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const presentationSlidesCost = selectedOptions.generatePresentationSlides
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_PRESENTATION_SLIDES * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const summaryCost = selectedOptions.generateSummary
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_SUMMARY * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const mindmapCost = selectedOptions.generateMindmap
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_MINDMAP * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const readabilityAnalysisCost = selectedOptions.generateReadabilityAnalysis
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_READABILITY_ANALYSIS * GLOBAL_PRICING_MULTIPLIER)
    : 0;
  const lessonPlanCost = selectedOptions.generateLessonPlan
    ? Math.ceil(NUMBER_OF_CREDITS_FOR_LESSON_PLAN * GLOBAL_PRICING_MULTIPLIER)
    : 0;

  const quizDiscount =
    selectedOptions.generateMultipleChoiceQuiz && selectedOptions.generateShortAnswerQuiz
      ? Math.ceil(NUMBER_OF_CREDITS_DISCOUNT_FOR_BOTH_QUIZZES * GLOBAL_PRICING_MULTIPLIER)
      : 0;

  const totalCost =
    baseCost +
    multipleChoiceCost +
    shortAnswerCost +
    ankiFlashcardsCost +
    presentationSlidesCost +
    summaryCost +
    mindmapCost +
    readabilityAnalysisCost +
    lessonPlanCost -
    quizDiscount;

  return {
    baseCost,
    multipleChoiceCost,
    shortAnswerCost,
    ankiFlashcardsCost,
    presentationSlidesCost,
    summaryCost,
    mindmapCost,
    readabilityAnalysisCost,
    lessonPlanCost,
    quizDiscount,
    totalCost,
  };
};

export const isEligibleForSupport = (job: ProcessingJob, processedFiles: FileData[]): boolean => {
  const status = job.job_status?.status || "";
  const isFailed =
    /(^|_)failed$/.test(status) ||
    status.includes("generation_failed") ||
    status === "processing_failed";
  if (isFailed) return true;
  if (job.job_status?.status === "completed") {
    if (processedFiles.length === 0) return true;
    const requiredFiles = [
      "final_optimized_document_markdown_file.md",
      "final_optimized_document.html",
      "final_optimized_document.pdf",
      "final_optimized_document__mobile.pdf",
    ];
    return !requiredFiles.every((filename) =>
      processedFiles.some((file) => file.filename === filename && isFileValid(file)),
    );
  }
  const progress = job.job_status?.progress_percentage;
  return progress === undefined || progress < 100;
};

export const isFileValid = (file: FileData): boolean => {
  const content = file.content;
  const isContentString = typeof content === "string";
  const contentLength = isContentString ? content.length : (content as ArrayBuffer).byteLength;

  if (contentLength < MIN_FILE_SIZE) return false;

  if (file.filename.endsWith(".md")) {
    return isContentString && isValidMarkdown(content as string);
  } else if (file.filename.endsWith(".pdf")) {
    return isContentString && !isPDFCorrupt(content as string);
  } else if (file.filename.endsWith(".html")) {
    return isContentString && isHTMLValid(content as string);
  }
  return true;
};

export const isValidMarkdown = (content: string): boolean => {
  const hasHeaders = /^#{1,6}\s.+/m.test(content);
  const hasParagraphs = /\n\n.+/m.test(content);
  return hasHeaders && hasParagraphs;
};

export const isPDFCorrupt = (content: string): boolean => {
  const isPDFHeader = content.startsWith("%PDF-");
  const hasEOFMarker = content.includes("%%EOF");
  return !isPDFHeader || !hasEOFMarker;
};

export const isHTMLValid = (content: string): boolean => {
  return /<html.*>[\s\S]*<\/html>/i.test(content);
};

export const getGeneratedDocumentTypes = async (jobId: string): Promise<Set<string>> => {
  try {
    const response = await axios.get(`/api/download/${jobId}`, {
      responseType: "blob",
    });

    const zip = new JSZip();
    await zip.loadAsync(response.data);
    const files = Object.keys(zip.files);

    const generatedTypes = new Set<string>();

    // Map file patterns to document types
    const filePatterns = {
      multiple_choice_quiz: ["multiple_choice_quiz.md", "multiple_choice_quiz.html"],
      short_answer_quiz: ["short_answer_quiz.md", "short_answer_quiz.html"],
      anki_flashcards: ["anki_flashcards.apkg", "flash_cards.csv"],
      presentation_slides: ["presentation_slides.pdf", "presentation_slides.tex"],
      summary: ["summary.md", "summary.html"],
      mindmap: ["mindmap_outline.md", "mermaid_mindmap.txt"],
      readability_analysis: ["readability_heatmap.html", "grade_level_version"],
      lesson_plan: ["lesson_plan.md"],
    };

    Object.entries(filePatterns).forEach(([docType, patterns]) => {
      if (patterns.some((pattern) => files.some((file) => file.includes(pattern)))) {
        generatedTypes.add(docType);
      }
    });

    return generatedTypes;
  } catch (error) {
    console.error("Error analyzing zip contents:", safeStringify(error));
    return new Set();
  }
};
